import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import styled from 'styled-components';

import ButtonGroup from 'ravenjs/lib/ButtonGroup';
import Col from 'ravenjs/lib/Col';
import Pagination from 'ravenjs/lib/Pagination';
import Row from 'ravenjs/lib/Row';
import SearchInput from 'ravenjs/lib/SearchInput';
import Toolbar from 'ravenjs/lib/Toolbar';
import Typography from 'ravenjs/lib/Typography';
import { MenuIcon, GridIcon } from 'ravenjs/lib/SvgIcon';
import { calculateSort } from 'ravenjs/utils/sorting';
import { extractAPIConfig, fetchAPIData } from 'ravenjs/utils/apis';
import { getHandler } from 'ravenjs/utils/actions';
import { mergeObjects } from 'ravenjs/utils/merge';
import { warning } from 'ravenjs/utils/warning';

import DataGrid from 'ravenjs/components/DataGrid';
import DataList from 'ravenjs/components/DataList';

const DataViewSearch = styled.div`
    max-width: 300px;
    width: 100%;
`;

const DataViewStyled = styled.div``;

class DataView extends React.Component {
    static propTypes = {
        /**
         * A map of action handlers used by rendered components.
         */
        actions: PropTypes.object,
        /**
         * The main data for the body of the component.
         */
        data: PropTypes.array,
        /**
         * An `oAuth` token to make API calls.
         */
        oAuthToken: PropTypes.string,
        onChangePage: PropTypes.func,
        onChangeRowsPerPage: PropTypes.func,
        /**
         * The JSON schema for the `DataView` and its inner components.
         * These include the `DataList` and `DataGrid` components.
         */
        schema: PropTypes.object,
        /**
         * Add a custom theme to the rendered `DataView` and its inner components.
         */
        theme: PropTypes.object,
    };

    static defaultProps = {
        actions: null,
        data: null,
        oAuthToken: null,
        onChangePage: null,
        onChangeRowsPerPage: null,
        schema: null,
        theme: null,
    };

    constructor(props) {
        super(props);
        // Schema
        const { schema } = props;
        // Extract the default view from the schema
        let view = _get(schema, 'viewSchema.default', 'list');
        // Double-check to see if the view is either a 'list' or 'grid'.
        if (!['list', 'grid'].includes(view)) {
            view = 'list';
        }
        // Construct the default state.
        this.state = {
            currentPage: 1,
            data: null,
            /**
             * TODO: Remove this `extraKeys` feature from the component
             * once we switch to the new full-React-supported application.
             * @experimental
             */
            extraKeys: {},
            order: _get(schema, 'sorting.default.order', ''),
            orderBy: _get(schema, 'sorting.default.orderBy', ''),
            rowsPerPage: _get(schema, 'pagination.rowsPerPage', 5),
            searchSpec: null,
            totalRows: 0,
            view,
        };
    }

    componentDidMount() {
        // Fetch the data upon mounting.
        this.fetchData({ ...this.state, props: this.props });
    }

    componentDidUpdate(prevProps, prevState) {
        const { data, schema, oAuthToken } = this.props;
        const { currentPage, order, orderBy, rowsPerPage, searchSpec } = this.state;
        const filteredPrevState = _omit(prevState, ['data', 'totalRows', 'view', 'extraKeys']);
        const filteredState = _omit(this.state, ['data', 'totalRows', 'view', 'extraKeys']);
        // Verify some properties to be different.
        const schemasDiff = !_isEqual(prevProps.schema, schema);
        const dataDiff = !_isEqual(prevProps.data, data);
        const oAuthDiff = !_isEqual(prevProps.oAuthToken, oAuthToken);
        const statesDiff = !_isEqual(filteredPrevState, filteredState);
        // If some of the properties are different, fetch the data.
        if (oAuthDiff || dataDiff || schemasDiff || statesDiff) {
            this.fetchData({
                currentPage: schemasDiff ? 1 : currentPage,
                order: schemasDiff ? _get(schema, 'sorting.default.order', '') : order,
                orderBy: schemasDiff ? _get(schema, 'sorting.default.orderBy', '') : orderBy,
                props: this.props,
                resetData: schemasDiff,
                rowsPerPage: schemasDiff && rowsPerPage,
                searchSpec,
            });
        }
    }

    fetchData = ({
        currentPage,
        loadingText,
        order,
        orderBy,
        props,
        resetData,
        rowsPerPage,
        searchSpec,
    }) => {
        const { actions, data: dataProps, oAuthToken, schema } = props;
        const {
            currentPage: currentPageFromState,
            rowsPerPage: rowsPerPageFromState,
            totalRows,
        } = this.state;
        const { baseUrl } = schema;
        const apis = _get(schema, 'apis');
        const formatParamsFunc = getHandler(actions, schema.formatParamsFuncRef);

        if (resetData) {
            // Update the state to reset the data.
            this.setState(prevState => ({
                currentPage: currentPage || prevState.currentPage,
                data: [],
                order,
                orderBy,
                rowsPerPage: rowsPerPage || prevState.rowsPerPage,
                searchSpec: '',
                totalRows: 0,
            }));
        }

        if (_isEmpty(dataProps) && !_isEmpty(apis)) {
            // Throw a warning if there is no `oAuthToken` found.
            warning(!_isEmpty(oAuthToken), [
                `A <DataView /> component requires an oAuthToken to fetch the requested data.`,
            ]);
            // Fire up the loading indicator.
            this.onLoad(props, true);
            // Extract some info from the `schema`.
            const api = extractAPIConfig(apis, 'data');
            const request = _get(api, 'request', {});
            const dataKey = _get(api, 'dataKey', 'data');
            const paramsMap = _get(api, 'paramsMap', {});
            const totalRowsKey = _get(api, 'totalKey', 'totalRecords');
            /**
             * TODO: Remove this `extraKeys` feature from the component
             * once we switch to the new full-React-supported application.
             * @experimental
             */
            const extraKeys = _get(api, 'extraKeys', []).reduce(
                (accum, val) => ({ ...accum, [val]: val }),
                {}
            );
            const {
                limitKey = 'limit',
                offsetKey = 'offset',
                searchSpecKey = 'searchSpec',
                sortKey = 'sort',
            } = paramsMap;
            const Authorization = _get(request, 'headers.Authorization');
            // Build the `offset`.
            const maxPages =
                rowsPerPageFromState > 0 ? Math.ceil(totalRows / rowsPerPageFromState) : 0;
            const offset =
                maxPages === 0 || maxPages < currentPageFromState
                    ? 0
                    : (currentPageFromState - 1) * rowsPerPageFromState;
            // Build the `sort`.
            const sort = calculateSort(order, orderBy);
            const params =
                typeof formatParamsFunc === 'function'
                    ? formatParamsFunc({
                          currentPage: currentPageFromState,
                          maxPages,
                          offset,
                          order,
                          orderBy,
                          paramsMap,
                          rowsPerPage: rowsPerPageFromState,
                          searchSpec,
                          sort,
                          totalRows,
                      })
                    : {
                          [limitKey]: rowsPerPageFromState,
                          [offsetKey]: offset,
                          [sortKey]: sort,
                          [searchSpecKey]: searchSpec,
                      };
            // Build the `axios` config.
            const config = mergeObjects(request, {
                baseURL: baseUrl || _get(request, 'baseURL'),
                headers: {
                    Authorization: Authorization || `Bearer ${oAuthToken}`,
                },
                params,
            });
            // Catch case for the `oAuthToken`.
            if (!_isEmpty(oAuthToken) && !_isEmpty(config.baseURL)) {
                // Fetch the data and update the state.
                fetchAPIData(config, { data: dataKey, totalRows: totalRowsKey, ...extraKeys })
                    .then(({ data: respData, totalRows: respTotalRows, ...respExtraKeys }) => {
                        const data = Array.isArray(respData) ? respData : null;
                        // Throw a warning if the returned `data` is not an Array.
                        warning(Array.isArray(respData), [
                            `<DataView /> requires a list of objects as its internal data.`,
                            `The API call returned 'data' of type '${typeof respData}'.`,
                            `Please check your 'schema.apis.dataKey' value.`,
                        ]);
                        // At times the `totalRows` gets returned as a String.
                        const totalRows = (respTotalRows && parseInt(respTotalRows, 10)) || 0;
                        // Update the state, turn off the loading indicator.
                        this.setState(
                            () => ({
                                data,
                                totalRows,
                                /**
                                 * TODO: Remove this `extraKeys` feature from the component
                                 * once we switch to the new full-React-supported application.
                                 * @experimental
                                 */
                                extraKeys: {
                                    ...respExtraKeys,
                                },
                            }),
                            () => this.onLoad(this.props, false)
                        );
                    })
                    .catch(error => {
                        // Update the state, turn off the loading indicator.
                        this.setState(
                            () => ({ error }),
                            () => this.onLoad(this.props, false)
                        );
                    });
            } else {
                // We have a missing `oAuthToken`.
                this.onLoad(this.props, false);
            }
        } else {
            // Throw a warning if the passed in `data` is not an Array.
            warning(Array.isArray(dataProps), [
                `<DataView /> requires a list of objects as its internal data.`,
                `The passed in 'data' prop is of type '${typeof dataProps}'`,
                `Please check the passed in 'data' prop.`,
            ]);
            // Set the loading to false when we have data passed in from props.
            this.onLoad(this.props, false);
            // Update the state.
            this.setState(() => ({
                data: Array.isArray(dataProps) ? dataProps : null,
                totalRows: Array.isArray(dataProps) ? dataProps.length : 0,
            }));
        }
    };

    onLoad = (props, loading) => {
        const { actions, schema } = props;
        const onLoadRef = _get(schema, 'onLoadRef');
        const onLoad = getHandler(actions, onLoadRef);
        // Update the loading indicator.
        return typeof onLoad === 'function' && onLoad(loading);
    };

    handlePageChange = (event, page) => {
        const { onChangePage } = this.props;
        // Set the current page on the state
        page && this.setState(() => ({ currentPage: page }));
        // If onChangePage is defined, we fire it with current page
        typeof onChangePage === 'function' && onChangePage(page);
    };

    handleRowsPerPageChange = rowsPerPage => {
        const { onChangeRowsPerPage } = this.props;
        const value = _get(rowsPerPage, 'value');
        // Set the current rows per page on the state
        value && this.setState(() => ({ rowsPerPage: value }));
        // If onChangeRowsPerPage is defined, we fire it with new rows per page
        typeof onChangeRowsPerPage === 'function' && onChangeRowsPerPage(value);
    };

    handleSearchKeyPress = e => {
        // If we are pressing the 'Enter' key,
        // let's update the search spec.
        // (e.key === 'Enter') === (charCode === 13)
        if (e.charCode === 13) {
            e.preventDefault();
            e.target.blur();
            this.updateSearchSpec(e);
        }
    };

    handleSearchOnCancel = e => {
        e.preventDefault();
        // Nullify the search spec.
        return this.updateSearchSpec({ target: { value: '' } });
    };

    handleSearchOnClick = value => {
        // If value is present, let's update the search spec.
        return value && this.updateSearchSpec({ target: { value } });
    };

    handleSearchOnChange = value => {
        // If the value is empty, we want to clear the search spec.
        return value === '' && this.updateSearchSpec({ target: { value } });
    };

    handleSortChange = (order, orderBy) => () => {
        const newOrder = order && order === 'asc' ? 'desc' : 'asc';
        // Set the current order and orderBy on the state
        newOrder &&
            orderBy &&
            this.setState(prevState => ({
                order: prevState.orderBy === orderBy ? newOrder : 'asc',
                orderBy,
            }));
    };

    switchViews = (view = 'list') => () => {
        // Update the state with the new view.
        this.setState(() => ({ view }));
    };

    updateSearchSpec = event => {
        // Extract the value from the current event
        const value = _get(event, 'target.value');
        // Update the state with the new search spec.
        return (value === '' || value) && this.setState(() => ({ searchSpec: value }));
    };

    renderButtonSwitcher = () => {
        const { schema } = this.props;
        const { view } = this.state;
        const initialSwitcherSchema = _get(schema, 'viewSchema.switcher', {});
        const btnTexts = _get(schema, 'viewSchema.switcher.text', {});
        // If the `switcher` schema prop is empty, we don't want to render anything.
        if (_isEmpty(initialSwitcherSchema)) {
            return null;
        }
        // Build the handlers list for the `ButtonGroup`.
        const handlers = {
            switchToGridView: this.switchViews('grid'),
            switchToListView: this.switchViews('list'),
        };
        // Build some `IconProps` for the `ButtonGroup` icons.
        const IconProps = {
            width: '24px',
            height: '24px',
        };
        // Build the buttons list for the `ButtonGroup`.
        const buttons = [
            {
                id: 'grid',
                icon: GridIcon,
                label: btnTexts.grid || 'Tile View',
                onClick: 'switchToGridView',
                IconProps,
            },
            {
                id: 'list',
                icon: MenuIcon,
                label: btnTexts.list || 'List View',
                onClick: 'switchToListView',
                IconProps,
            },
        ];
        // Update the `ButtonGroup` schema by omitting some values.
        const switcherSchema = {
            ..._omit(initialSwitcherSchema, ['text']),
        };
        // Render the `ButtonGroup`.
        return (
            <ButtonGroup
                {...switcherSchema}
                buttons={buttons}
                handlers={handlers}
                activeId={view}
                toggle
            />
        );
    };

    renderDataList = () => {
        const { actions, schema } = this.props;
        const { data, order, orderBy, view } = this.state;
        const listSchema = _get(schema, 'listSchema', {});
        const sortingSchema = _get(schema, 'sorting', {});
        // If the schema is empty, then don't render the DataList.
        if (_isEmpty(listSchema)) {
            return null;
        }
        // Make a copy of the `listSchema`
        let dataListSchema = {
            ...listSchema,
        };
        // Build the actions map.
        const dataListActions = {
            ...actions,
            onSortChange: this.handleSortChange,
        };
        // If sorting is required, then update the schema.
        if (_get(schema, 'sorting')) {
            dataListSchema = mergeObjects(dataListSchema, {
                sorting: {
                    ...sortingSchema,
                    order,
                    orderBy,
                },
            });
        }
        // If the current view is not the `list` view,
        // don't render anything.
        const style = {
            display: view !== 'list' ? 'none' : null,
        };
        // Render the `list` view, a.k.a `DataList`.
        return (
            <DataList style={style} actions={dataListActions} data={data} schema={dataListSchema} />
        );
    };

    renderDataGrid = () => {
        const { actions, schema } = this.props;
        const { data, view } = this.state;
        const gridSchema = _get(schema, 'gridSchema', {});
        // If the schema is empty, then don't render the DataGrid.
        if (_isEmpty(gridSchema)) {
            return null;
        }
        // If the current view is not the `grid` view,
        // don't render anything.
        const style = {
            display: view !== 'grid' ? 'none' : null,
        };
        // Render the `grid` view, a.k.a `DataGrid`.
        return <DataGrid style={style} actions={actions} data={data} schema={gridSchema} />;
    };

    renderBottomPagination = () => {
        // Extract the pagination position (top, bottom, both ?)
        const position = _get(this.props, 'schema.pagination.position', null);
        // If the position is bottom, then render pagination
        return position === 'bottom' || position === 'both' ? this.renderPagination() : null;
    };

    renderPagination = () => {
        const { currentPage, rowsPerPage, totalRows } = this.state;
        // Extract the pagination schema from the schema
        const paginationSchema = _get(this.props, 'schema.pagination', {});
        // Construct the pagination properties
        const paginationProps = {
            currentPage,
            onChangePage: this.handlePageChange,
            onChangeRowsPerPage: this.handleRowsPerPageChange,
            rowsPerPage: rowsPerPage || paginationSchema.rowsPerPage,
            totalRows,
            style: {
                padding: '20px',
            },
        };
        // If we have a valid element, render it
        return <Pagination {...paginationSchema} {...paginationProps} />;
    };

    renderSearch = () => {
        const { schema } = this.props;
        // Extract the search spec from the schema
        const searchSchema = _get(schema, 'search', null);
        // If the `searchSchema` is empty,
        // don't render anything.
        if (_isEmpty(searchSchema)) {
            return null;
        }
        // Extract the placeholder from the `searchSchema`
        const placeholder = _get(searchSchema, 'placeholder', 'Search');
        // Build the search input props
        const searchInputProps = {
            onCancel: this.handleSearchOnCancel,
            onChange: this.handleSearchOnChange,
            onClick: this.handleSearchOnClick,
            onKeyPress: this.handleSearchKeyPress,
            placeholder,
        };
        // Render the search field.
        return (
            <Row justify="center" gutter={false}>
                <DataViewSearch>
                    <SearchInput {...searchInputProps} />
                </DataViewSearch>
            </Row>
        );
    };

    renderToolbar = () => {
        const { schema } = this.props;
        // Extract the toolbar props
        const toolbarProps = _get(schema, 'toolbar', null);
        // Render the Toolbar.
        return (
            <Toolbar margin="0" {...toolbarProps}>
                <Row gutter={false} alignItems="flex-end">
                    <Col gutter={false}>
                        {this.renderToolbarTitle()}
                        {this.renderButtonSwitcher()}
                    </Col>
                    <Col gutter={false}>{this.renderSearch()}</Col>
                    <Col gutter={false}>
                        <Row gutter={false} justify="flex-end">
                            {this.renderToolbarActions()}
                        </Row>
                    </Col>
                </Row>
            </Toolbar>
        );
    };

    renderToolbarActions = () => {
        const { actions, schema } = this.props;
        // Extract the actions schema.
        const actionsSchema = _get(schema, 'actions', null);
        // If the `actionsSchema` is empty,
        // don't render anything.
        if (_isEmpty(actionsSchema)) {
            return null;
        }
        // Render the toolbar actions.
        return <ButtonGroup buttons={actionsSchema} handlers={actions} />;
    };

    renderToolbarTitle = () => {
        const { schema } = this.props;
        const { totalRows } = this.state;
        // Extract the title schema.
        const titleSchema = _get(schema, 'title', null);
        // If the `titleSchema` is empty,
        // don't render anything.
        if (_isEmpty(titleSchema)) {
            return null;
        }
        // Extract props from the schema.
        const { title = '', totalCount = null, ...rest } = titleSchema;
        // Render the toolbar title.
        return (
            <Typography type="subheading" {...rest}>
                {title}
                {totalCount && ` (${totalRows})`}
            </Typography>
        );
    };

    renderTopPagination = () => {
        // Extract the pagination position (top, bottom, both ?)
        const position = _get(this.props, 'schema.pagination.position', null);
        // If the position is top, then render pagination
        return position === 'top' || position === 'both' ? this.renderPagination() : null;
    };

    render() {
        return (
            <DataViewStyled>
                {this.renderToolbar()}
                {this.renderTopPagination()}
                {this.renderDataList()}
                {this.renderDataGrid()}
                {this.renderBottomPagination()}
            </DataViewStyled>
        );
    }
}

export default DataView;
